// PPCContractors.js  — Contractor Master (rebuilt clean)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PPC_API_URL } from './PPCContext';

const MODULES = [
  { key: 'BRAVO', label: 'BRAVO', color: '#d32f2f', bg: '#fff3e0' },
  { key: 'PAPA',  label: 'PAPA',  color: '#1b5e20', bg: '#e8f5e9' },
  { key: 'ROMEO', label: 'ROMEO', color: '#0d47a1', bg: '#e3f2fd' },
  { key: 'GOLF',  label: 'GOLF',  color: '#4a148c', bg: '#f3e5f5' },
];

const AVATAR_COLORS = [
  '#d32f2f','#1565c0','#059669','#d97706','#7b1fa2',
  '#0097a7','#e64a19','#388e3c','#1976d2','#c62828',
];

const empty = () => ({
  name:'', code:'', contact_person:'', phone:'',
  email:'', location:'', address:'', notes:'',
  modules:['BRAVO'], is_active: true,
});

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const initials = (n) => (n||'?').split(' ').map(w=>w[0]).join('').substring(0,2).toUpperCase();
const avatarBg  = (i) => AVATAR_COLORS[i % AVATAR_COLORS.length];

// ─────────────────────────────────────────────────────────────────────────────
export default function PPCContractors() {
  const navigate = useNavigate();

  const [rows,       setRows]       = useState([]);
  const [summary,    setSummary]    = useState({ total:0, active:0, inactive:0 });
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [modFilter,  setModFilter]  = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [toast,      setToast]      = useState(null);

  // Modal state
  const [showForm,   setShowForm]   = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState(empty());
  const [errors,     setErrors]     = useState({});
  const [saving,     setSaving]     = useState(false);

  // Delete confirm
  const [delRow,     setDelRow]     = useState(null);
  const [deleting,   setDeleting]   = useState(false);

  // ── Toast ────────────────────────────────────────────────────────────────
  const toast$ = (msg, type='success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${PPC_API_URL}/contractors.php?action=list&limit=500`, auth()
      );
      if (res.data?.success) {
        const all = res.data.contractors || [];
        setRows(all);
        setSummary({
          total:    all.length,
          active:   all.filter(c => c.is_active).length,
          inactive: all.filter(c => !c.is_active).length,
        });
      }
    } catch (e) {
      toast$('Failed to load contractors', 'error');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Filter ───────────────────────────────────────────────────────────────
  const filtered = rows.filter(c => {
    const q = search.toLowerCase();
    const matchQ = !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q) || (c.contact_person||'').toLowerCase().includes(q);
    const matchM = !modFilter || (Array.isArray(c.modules) && c.modules.includes(modFilter));
    const matchS = !statusFilter || (statusFilter==='active' ? c.is_active : !c.is_active);
    return matchQ && matchM && matchS;
  });

  // ── Form helpers ─────────────────────────────────────────────────────────
  const setF = (k, v) => { setForm(p => ({...p,[k]:v})); setErrors(p=>({...p,[k]:''})); };

  const toggleMod = (m) => setForm(p => ({
    ...p,
    modules: p.modules.includes(m) ? p.modules.filter(x=>x!==m) : [...p.modules, m]
  }));

  const openAdd = () => { setEditId(null); setForm(empty()); setErrors({}); setShowForm(true); };

  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      name: c.name||'', code: c.code||'',
      contact_person: c.contact_person||'', phone: c.phone||'',
      email: c.email||'', location: c.location||'',
      address: c.address||'', notes: c.notes||'',
      modules: Array.isArray(c.modules) ? [...c.modules] : ['BRAVO'],
      is_active: !!c.is_active,
    });
    setErrors({});
    setShowForm(true);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = 'Name is required';
    if (!form.code.trim())    e.code    = 'Code is required';
    if (form.modules.length===0) e.modules = 'Select at least one module';
    // duplicate check
    const nm = form.name.trim().toUpperCase();
    const cd = form.code.trim().toUpperCase();
    if (rows.find(r => r.name.toUpperCase()===nm && r.id!==editId)) e.name = 'Name already exists';
    if (rows.find(r => r.code.toUpperCase()===cd && r.id!==editId)) e.code = 'Code already used';
    setErrors(e);
    return Object.keys(e).length===0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload = { ...form, name: form.name.trim().toUpperCase(), code: form.code.trim().toUpperCase() };
    try {
      let res;
      if (editId) {
        payload.id = editId;
        res = await axios.put(`${PPC_API_URL}/contractors.php?action=update`, payload, auth());
      } else {
        res = await axios.post(`${PPC_API_URL}/contractors.php?action=create`, payload, auth());
      }
      if (res.data?.success) {
        toast$(editId ? 'Contractor updated!' : 'Contractor added!');
        setShowForm(false);
        fetchAll();
      } else {
        toast$(res.data?.error || 'Save failed', 'error');
      }
    } catch (e) {
      toast$(e.response?.data?.error || 'Save failed', 'error');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!delRow) return;
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${PPC_API_URL}/contractors.php?action=delete&id=${delRow.id}&hard=1`, auth()
      );
      if (res.data?.success) {
        toast$('Contractor deleted');
        setDelRow(null);
        fetchAll();
      } else {
        toast$(res.data?.error || 'Delete failed', 'error');
      }
    } catch (e) {
      toast$(e.response?.data?.error || 'Delete failed', 'error');
    }
    setDeleting(false);
  };

  const handleToggle = async (c) => {
    try {
      const res = await axios.put(
        `${PPC_API_URL}/contractors.php?action=toggle-status`, { id: c.id }, auth()
      );
      if (res.data?.success) { toast$(res.data.message || 'Status updated'); fetchAll(); }
    } catch (e) { toast$('Failed to update status', 'error'); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>

      {/* Header */}
      <header style={S.hdr}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button style={S.hbtn} onClick={() => navigate('/ppc')}>← Back</button>
          <span style={S.htitle}>👷 Contractor Master</span>
        </div>
        <button style={S.addBtn} onClick={openAdd}>＋ Add Contractor</button>
      </header>

      {/* Stat cards */}
      <div style={S.statRow}>
        {[
          { label:'Total',    value: summary.total,    color:'#1565c0' },
          { label:'Active',   value: summary.active,   color:'#059669' },
          { label:'Inactive', value: summary.inactive, color:'#9ca3af' },
        ].map((s,i) => (
          <div key={i} style={S.statCard}>
            <div style={{ fontSize:28, fontWeight:800, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:11, color:'#6b7280', marginTop:3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={S.toolbar}>
        <input
          placeholder="Search name, code, contact..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={S.searchInp}
        />
        <select value={modFilter} onChange={e=>setModFilter(e.target.value)} style={S.sel}>
          <option value="">All Modules</option>
          {MODULES.map(m => <option key={m.key} value={m.key}>{m.key}</option>)}
        </select>
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={S.sel}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <span style={{ fontSize:11, color:'#9ca3af', marginLeft:'auto' }}>
          {filtered.length} record{filtered.length!==1?'s':''}
        </span>
      </div>

      {/* Table */}
      <div style={S.card}>
        {loading ? (
          <div style={S.center}>Loading contractors…</div>
        ) : filtered.length===0 ? (
          <div style={S.center}>No contractors found</div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={S.tbl}>
              <thead>
                <tr>
                  {['#','Contractor','Code','Contact','Phone','Modules','Status','Actions'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, idx) => (
                  <tr key={c.id} style={S.tr}
                    onMouseEnter={e=>e.currentTarget.style.background='#f5f7ff'}
                    onMouseLeave={e=>e.currentTarget.style.background=''}>
                    <td style={{...S.td, color:'#9ca3af', width:36}}>{idx+1}</td>
                    <td style={S.td}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:34, height:34, borderRadius:'50%', background:avatarBg(idx), display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#fff', flexShrink:0 }}>
                          {initials(c.name)}
                        </div>
                        <div>
                          <div style={{ fontWeight:700, fontSize:12 }}>{c.name}</div>
                          <div style={{ fontSize:10, color:'#9ca3af' }}>{c.location||'—'}</div>
                        </div>
                      </div>
                    </td>
                    <td style={S.td}>
                      <span style={{ fontFamily:'monospace', fontSize:11, fontWeight:700, color:'#1565c0' }}>{c.code}</span>
                    </td>
                    <td style={S.td}>{c.contact_person||'—'}</td>
                    <td style={S.td}>{c.phone||'—'}</td>
                    <td style={{...S.td, textAlign:'center'}}>
                      <div style={{ display:'flex', gap:4, flexWrap:'wrap', justifyContent:'center' }}>
                        {Array.isArray(c.modules) && c.modules.length===4
                          ? <span style={S.modBadge('#374151','#f0f2f5')}>ALL</span>
                          : (Array.isArray(c.modules)?c.modules:[]).map(m => {
                              const mod = MODULES.find(x=>x.key===m);
                              return <span key={m} style={S.modBadge(mod?.color||'#555', mod?.bg||'#eee')}>{m}</span>;
                            })
                        }
                      </div>
                    </td>
                    <td style={{...S.td, textAlign:'center'}}>
                      <button onClick={()=>handleToggle(c)}
                        style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11, fontWeight:600, color:c.is_active?'#059669':'#9ca3af', background:'transparent', border:'none', cursor:'pointer', padding:'3px 8px', borderRadius:12 }}>
                        <span style={{ width:7, height:7, borderRadius:'50%', background:c.is_active?'#059669':'#9ca3af', display:'inline-block' }}/>
                        {c.is_active?'Active':'Inactive'}
                      </button>
                    </td>
                    <td style={{...S.td, textAlign:'center'}}>
                      <div style={{ display:'flex', gap:5, justifyContent:'center' }}>
                        <button onClick={()=>openEdit(c)} style={S.actBtn('#1565c0')}>EDIT</button>
                        <button onClick={()=>setDelRow(c)} style={S.actBtn('#d32f2f')}>DELETE</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── ADD / EDIT MODAL ── */}
      {showForm && (
        <div style={S.overlay} onClick={()=>setShowForm(false)}>
          <div style={S.modal} onClick={e=>e.stopPropagation()}>
            <div style={S.mHdr}>
              <div>
                <div style={{ color:'#fff', fontSize:15, fontWeight:700 }}>
                  {editId ? '✏️ Edit Contractor' : '➕ Add Contractor'}
                </div>
                <div style={{ color:'rgba(255,255,255,.7)', fontSize:11, marginTop:2 }}>
                  {editId ? form.name : 'Fill in contractor details'}
                </div>
              </div>
              <button onClick={()=>setShowForm(false)} style={S.xBtn}>×</button>
            </div>

            <div style={{ padding:'20px', display:'grid', gap:14 }}>
              {/* Name + Code */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <Field label="Name *" error={errors.name}>
                  <input value={form.name} onChange={e=>setF('name',e.target.value.toUpperCase())}
                    placeholder="e.g. DBS CONTRACTORS" style={inp(errors.name)} />
                </Field>
                <Field label="Code *" error={errors.code}>
                  <input value={form.code} onChange={e=>setF('code',e.target.value.toUpperCase())}
                    placeholder="e.g. DBS" style={inp(errors.code)} maxLength={20} />
                </Field>
              </div>

              {/* Contact + Phone */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <Field label="Contact Person">
                  <input value={form.contact_person} onChange={e=>setF('contact_person',e.target.value)}
                    placeholder="Full name" style={inp()} />
                </Field>
                <Field label="Phone">
                  <input value={form.phone} onChange={e=>setF('phone',e.target.value)}
                    placeholder="+91 00000 00000" style={inp()} />
                </Field>
              </div>

              {/* Email + Location */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <Field label="Email">
                  <input type="email" value={form.email} onChange={e=>setF('email',e.target.value)}
                    placeholder="email@example.com" style={inp()} />
                </Field>
                <Field label="Location">
                  <input value={form.location} onChange={e=>setF('location',e.target.value)}
                    placeholder="City or area" style={inp()} />
                </Field>
              </div>

              {/* Modules */}
              <div>
                <div style={S.secDiv}>Module Assignment</div>
                {errors.modules && <span style={{ fontSize:11, color:'#d32f2f' }}>{errors.modules}</span>}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginTop:8 }}>
                  {MODULES.map(m => {
                    const on = form.modules.includes(m.key);
                    return (
                      <div key={m.key} onClick={()=>toggleMod(m.key)}
                        style={{ padding:'10px 12px', border:`1.5px solid ${on?'#1565c0':'#e2e6ea'}`, borderRadius:8, cursor:'pointer', background:on?'#dbeafe':'#fff', transition:'all .15s' }}>
                        <div style={{ fontSize:12, fontWeight:700, color:on?'#1565c0':'#374151' }}>{m.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Status */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 14px', background:'#f8f9fa', borderRadius:8, border:'1px solid #e2e6ea' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600 }}>Active</div>
                  <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>
                    {form.is_active ? 'Contractor appears in dashboards' : 'Hidden from all dashboards'}
                  </div>
                </div>
                <Toggle checked={form.is_active} onChange={v=>setF('is_active',v)} />
              </div>

              {/* Notes */}
              <Field label="Notes">
                <textarea value={form.notes} onChange={e=>setF('notes',e.target.value)}
                  placeholder="Any additional notes..." rows={2}
                  style={{ ...inp(), resize:'vertical', fontFamily:'inherit' }} />
              </Field>
            </div>

            <div style={{ padding:'0 20px 20px', display:'flex', gap:10 }}>
              <button onClick={()=>setShowForm(false)} style={S.cancelBtn}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={S.saveBtn}>
                {saving ? 'Saving…' : editId ? 'Update Contractor' : 'Save Contractor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM ── */}
      {delRow && (
        <div style={S.overlay} onClick={()=>setDelRow(null)}>
          <div style={{ background:'#fff', borderRadius:16, padding:'30px 24px', width:'100%', maxWidth:380, textAlign:'center' }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ fontSize:44, marginBottom:14 }}>🗑️</div>
            <div style={{ fontSize:17, fontWeight:700, color:'#d32f2f', marginBottom:6 }}>Delete Contractor?</div>
            <div style={{ fontSize:13, color:'#6b7280', marginBottom:16 }}>
              Permanently delete <strong>{delRow.name}</strong> ({delRow.code})?
            </div>
            <div style={{ fontSize:11, color:'#d32f2f', background:'#fff5f5', border:'1px solid #fecaca', borderRadius:7, padding:'9px 12px', marginBottom:18, textAlign:'left' }}>
              ⚠️ This removes the contractor from all PPC dashboards.
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={()=>setDelRow(null)} style={S.cancelBtn}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ ...S.saveBtn, background:'linear-gradient(135deg,#dc2626,#ef4444)' }}>
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', bottom:22, right:22, padding:'11px 20px', borderRadius:9,
          background: toast.type==='error' ? '#d32f2f' : '#059669',
          color:'#fff', fontSize:13, fontWeight:500, zIndex:9999, boxShadow:'0 4px 18px rgba(0,0,0,.25)' }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
const Field = ({ label, error, children }) => (
  <div>
    <label style={{ display:'block', fontSize:10, fontWeight:700, color:'#6b7280', marginBottom:5, textTransform:'uppercase', letterSpacing:'.4px' }}>{label}</label>
    {children}
    {error && <span style={{ fontSize:10, color:'#d32f2f', marginTop:3, display:'block' }}>{error}</span>}
  </div>
);

const Toggle = ({ checked, onChange }) => (
  <label style={{ position:'relative', width:44, height:24, flexShrink:0, cursor:'pointer' }}>
    <input type="checkbox" checked={checked} onChange={e=>onChange(e.target.checked)}
      style={{ opacity:0, width:0, height:0, position:'absolute' }} />
    <span style={{ position:'absolute', inset:0, borderRadius:24, transition:'background .2s', background:checked?'#059669':'#d1d5db', cursor:'pointer' }}>
      <span style={{ position:'absolute', width:18, height:18, borderRadius:'50%', background:'#fff', top:3, left:checked?23:3, transition:'left .2s', boxShadow:'0 1px 4px rgba(0,0,0,.2)' }}/>
    </span>
  </label>
);

// ── Style helpers ────────────────────────────────────────────────────────────
const inp   = (err) => ({ width:'100%', padding:'9px 11px', borderRadius:7, border:`1.5px solid ${err?'#d32f2f':'#e2e6ea'}`, fontFamily:'inherit', fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' });
const S = {
  page:     { minHeight:'100vh', background:'#f0f2f5', fontFamily:"'DM Sans',sans-serif", fontSize:13 },
  hdr:      { background:'linear-gradient(135deg,#d32f2f,#1565c0)', padding:'0 24px', height:56, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100, boxShadow:'0 2px 12px rgba(211,47,47,.3)' },
  hbtn:     { background:'rgba(255,255,255,.18)', border:'1px solid rgba(255,255,255,.3)', color:'#fff', padding:'6px 14px', borderRadius:20, cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:500 },
  htitle:   { color:'#fff', fontSize:16, fontWeight:700 },
  addBtn:   { background:'#059669', border:'none', color:'#fff', padding:'8px 18px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700, fontFamily:'inherit' },
  statRow:  { display:'flex', gap:14, padding:'20px 24px 0' },
  statCard: { background:'#fff', borderRadius:12, padding:'16px 22px', border:'1px solid #e2e6ea', textAlign:'center', minWidth:110 },
  toolbar:  { display:'flex', gap:10, padding:'14px 24px', alignItems:'center', flexWrap:'wrap' },
  searchInp:{ flex:1, maxWidth:320, padding:'8px 12px', border:'1px solid #e2e6ea', borderRadius:8, fontFamily:'inherit', fontSize:12, outline:'none', background:'#fff' },
  sel:      { padding:'8px 12px', border:'1px solid #e2e6ea', borderRadius:8, fontFamily:'inherit', fontSize:12, background:'#fff', cursor:'pointer', minWidth:130 },
  card:     { margin:'0 24px 30px', background:'#fff', borderRadius:14, border:'1px solid #e2e6ea', overflow:'hidden', boxShadow:'0 2px 12px rgba(0,0,0,.06)' },
  center:   { padding:'48px', textAlign:'center', color:'#9ca3af', fontSize:13 },
  tbl:      { width:'100%', borderCollapse:'collapse', fontSize:12 },
  th:       { padding:'10px 12px', background:'#f8f9fa', borderBottom:'1px solid #e2e6ea', fontWeight:700, fontSize:11, color:'#6b7280', textAlign:'left', whiteSpace:'nowrap' },
  tr:       { borderBottom:'1px solid #f3f4f6', transition:'background .1s' },
  td:       { padding:'10px 12px', verticalAlign:'middle' },
  modBadge: (c,bg) => ({ padding:'3px 8px', borderRadius:12, fontSize:10, fontWeight:700, color:c, background:bg, display:'inline-block' }),
  actBtn:   (bg)   => ({ padding:'4px 11px', border:'none', borderRadius:5, background:bg, color:'#fff', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }),
  overlay:  { position:'fixed', inset:0, background:'rgba(0,0,0,.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 },
  modal:    { background:'#fff', borderRadius:16, width:'100%', maxWidth:600, maxHeight:'93vh', overflowY:'auto' },
  mHdr:     { background:'linear-gradient(135deg,#d32f2f,#1565c0)', padding:'15px 20px', display:'flex', justifyContent:'space-between', alignItems:'flex-start', borderRadius:'16px 16px 0 0', position:'sticky', top:0, zIndex:10 },
  xBtn:     { width:28, height:28, borderRadius:'50%', border:'none', background:'rgba(255,255,255,.2)', color:'#fff', fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 },
  secDiv:   { fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.5px', borderBottom:'1px solid #e2e6ea', paddingBottom:6, marginBottom:4 },
  cancelBtn:{ flex:1, padding:11, borderRadius:8, border:'1.5px solid #e2e6ea', background:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer' },
  saveBtn:  { flex:2, padding:11, borderRadius:8, border:'none', background:'linear-gradient(135deg,#d32f2f,#1565c0)', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer' },
};
